import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { breakpoints, BREAKPOINTS_NAMES, BreakpointSizesType } from '@pickit/pickit-grid-system';
import { get, update } from 'lodash';
import { TableProps } from './types';
import {
  CollapsedRow,
  TableCollapsibleRow,
  TableData,
  TableHeaders,
  NoResultRow,
  TableCollapsibleContainer,
  RowsContainer,
  TableTitle,
  TableTitleWrapper,
  TooltipWrapper,
  TablePaginatorContainer,
  TableContainer,
} from '../SharedTable/styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Collapsible from '../Collapsible/Collapsible';
import Checkbox from '../Checkbox/Checkbox';
import ViewMoreButton from './components/ViewMoreButton/ViewMoreButton';
import Paginator from '../Paginator/Paginator';
import useTable from '../utils/useTable';
import TableCell from '../SharedTable/components/TableCell';
import Tooltip from '../Tooltip/Tooltip';
import BasicIcon from '../BasicIcon/BasicIcon';

const Table = <T,>(props: TableProps<T>) => {
  const {
    headers,
    data,
    pagination,
    viewMoreMessage,
    pages,
    groupBy,
    selectAllEnable = true,
    noResultsText = 'No encontramos resultados para la búsqueda.',
    hasMoreRows = false,
    rowCount = 5,
    size = 'small',
    dynamicHeight = false,
    testId,
    fontSize = 'small',
    isClickableRow,
    onClickRow,
    loadMoreRows,
    onChangePage,
  } = props;

  const { actualTheme } = useContext(ThemeContext);
  const [disableAllCheck, setDisableAllCheck] = useState(true);
  const [processedData, setProcessedData] = useState(data);

  const { rowsContainerRef, renderCellContent, rowProps } = useTable<T>({
    ...props,
    processedData,
    disableAllCheck,
    setDisableAllCheck,
  });

  const [isCollapsible, setIsCollapsible] = useState(false);
  const [maxCollapsibleBreakpoint, setMaxCollapsibleBreakpoint] = useState<BreakpointSizesType>();
  const [currentBreakpoint, setCurrentBreakpoint] = useState<BreakpointSizesType>('md');
  const checkClickableRow = useCallback(
    (row: T) => !!onClickRow && ((!!isClickableRow && isClickableRow(row)) || !isClickableRow),
    [isClickableRow, onClickRow],
  );

  const onScroll = useCallback(() => {
    const { scrollHeight, scrollTop, clientHeight } = rowsContainerRef.current;
    const isScrollBottom = scrollHeight <= scrollTop + clientHeight && hasMoreRows;
    if (isScrollBottom) {
      loadMoreRows();
    }
  }, [hasMoreRows, loadMoreRows, rowsContainerRef]);

  useEffect(() => {
    setProcessedData(() => data);
  }, [data]);

  useEffect(() => {
    const rowsContainer = rowsContainerRef?.current;
    if (rowsContainer) {
      if (pagination === 'infinite-scroll') {
        rowsContainer.addEventListener('scroll', onScroll);
      } else {
        rowsContainer.removeEventListener('scroll', onScroll);
      }
    }
    return () => {
      if (rowsContainer) rowsContainer.removeEventListener('scroll', onScroll);
    };
  }, [onScroll, pagination, rowsContainerRef, data]);

  const collapseRows = useMemo(
    () =>
      (isCollapsible && breakpoints[currentBreakpoint] <= breakpoints[maxCollapsibleBreakpoint]) ||
      !!groupBy,
    [currentBreakpoint, groupBy, isCollapsible, maxCollapsibleBreakpoint],
  );

  const checkHeaderCollapsible = useCallback((collapsibleBreakpoint: BreakpointSizesType) => {
    if (collapsibleBreakpoint) {
      setMaxCollapsibleBreakpoint((prev) =>
        breakpoints[prev] > breakpoints[collapsibleBreakpoint] ? prev : collapsibleBreakpoint,
      );
      return true;
    }
    return false;
  }, []);

  const processGroupData = useCallback(() => {
    setProcessedData([]);
    const bufferData: any[] = [...data];
    while (bufferData.length > 0) {
      const searchValue = get(bufferData[0], groupBy);
      const indexes: number[] = [];
      const filteredData = bufferData.filter((object: any, index: number) => {
        if (get(object, groupBy) === searchValue) {
          indexes.push(index);
          return true;
        }
        return false;
      });

      let newRow: T = {} as T;
      update(newRow as any, groupBy, () => searchValue);
      headers.forEach((header) => {
        if (header?.key !== groupBy && (header.groupLabels || header.isGroupTitle)) {
          const label =
            filteredData.length > 1 ? header.groupLabels?.plural : header.groupLabels?.singular;
          if (Array.isArray(header.key)) {
            header.key.forEach((key) => {
              newRow[key as keyof T] = header.isGroupTitle
                ? get(bufferData[0], key)
                : `${filteredData.length} ${label}`;
            });
          } else
            newRow[header.key as keyof T] = header.isGroupTitle
              ? get(bufferData[0], header.key)
              : `${filteredData.length} ${label}`;
        }
      });
      newRow = { ...newRow, collapsedRows: filteredData };

      setProcessedData((prev) => prev.concat([newRow]));
      indexes.reverse().forEach((i) => {
        bufferData.splice(i, 1);
      });
    }
  }, [data, groupBy, headers]);

  useEffect(() => {
    if (groupBy) {
      processGroupData();
    }
  }, [groupBy, processGroupData, data]);

  useEffect(() => {
    let setValue = false;
    headers.forEach((header) => {
      setValue = checkHeaderCollapsible(header.collapsible) || setValue;
    });
    setIsCollapsible(setValue);
  }, [checkHeaderCollapsible, headers]);

  const checkCurrentBreakpoint = useCallback(() => {
    BREAKPOINTS_NAMES.some((breakpoint) => {
      if (window.innerWidth > breakpoints[breakpoint]) {
        setCurrentBreakpoint(breakpoint);
        return true;
      }
      return false;
    });
  }, []);

  useEffect(() => {
    checkCurrentBreakpoint();
    window.addEventListener('resize', () => checkCurrentBreakpoint());
    return () => window.removeEventListener('resize', () => checkCurrentBreakpoint());
  }, [checkCurrentBreakpoint]);

  const collapseCol = useCallback(
    (collapsibleBreakpoint: BreakpointSizesType) =>
      collapsibleBreakpoint &&
      breakpoints[collapsibleBreakpoint] >= breakpoints[currentBreakpoint] &&
      !groupBy,
    [currentBreakpoint, groupBy],
  );

  const renderHeaders = useCallback(
    () => (
      <TableHeaders
        {...rowProps}
        collapse={collapseRows}
        size={size}
        dynamicHeight={dynamicHeight}
        fontSize={fontSize}
        hasScroll={
          rowCount < data.length ||
          (rowCount === data.length && pagination === 'view-more-button' && hasMoreRows)
        }
      >
        {headers.map(
          (header, i) =>
            !(
              isCollapsible &&
              breakpoints[currentBreakpoint] <= breakpoints[maxCollapsibleBreakpoint] &&
              collapseCol(header.collapsible)
            ) && (
              <TableCell header={header} index={i}>
                {header.type === 'checkbox' && selectAllEnable ? (
                  <Checkbox
                    data-testId={`${testId}-select-all`}
                    value={header.checkboxConfig.allSelected && header.checkboxConfig.allSelected()}
                    onChange={header.checkboxConfig?.selectAll}
                    disabled={disableAllCheck}
                  />
                ) : (
                  <TableTitle data-testId={`${testId}-title-${header.testId}`}>
                    <TableTitleWrapper align={header.align}>
                      <b>{header.label}</b>
                      {header.tooltip ? (
                        <TooltipWrapper>
                          <Tooltip
                            message={header.tooltip.message}
                            position={header.tooltip.position}
                          >
                            <BasicIcon color="#338AF3" name="information-circle" size="small" />
                          </Tooltip>
                        </TooltipWrapper>
                      ) : null}
                    </TableTitleWrapper>
                  </TableTitle>
                )}
              </TableCell>
            ),
        )}
      </TableHeaders>
    ),
    [
      rowProps,
      collapseRows,
      size,
      dynamicHeight,
      fontSize,
      rowCount,
      data.length,
      pagination,
      hasMoreRows,
      headers,
      isCollapsible,
      currentBreakpoint,
      maxCollapsibleBreakpoint,
      collapseCol,
      selectAllEnable,
      testId,
      disableAllCheck,
    ],
  );

  const shouldRenderTogglerContent = useCallback(
    (type) => (groupBy && type !== 'icon' && type !== 'button' && type !== 'checkbox') || !groupBy,
    [groupBy],
  );

  const renderCollapsibleToggler = useCallback(
    (row, isGroupTable, index) => (
      <TableData
        {...rowProps}
        hover={false}
        collapsible
        isGroupHeader={isGroupTable}
        size={size}
        dynamicHeight={dynamicHeight}
        fontSize={fontSize}
      >
        {headers.map(
          (header) =>
            !collapseCol(header.collapsible) && (
              <TableCell<T> header={header}>
                {shouldRenderTogglerContent(header.type) &&
                  (header.type && (!isGroupTable || header.isGroupTitle)
                    ? renderCellContent[header.type](header, row, index)
                    : renderCellContent.string(header, row, index))}
              </TableCell>
            ),
        )}
      </TableData>
    ),
    [
      collapseCol,
      dynamicHeight,
      fontSize,
      headers,
      renderCellContent,
      rowProps,
      shouldRenderTogglerContent,
      size,
    ],
  );

  const shouldRenderToggleContent = useCallback(
    (header) => (groupBy && !header.isGroupTitle && groupBy !== header.key) || !groupBy,
    [groupBy],
  );

  const renderCollapsibleToggle = useCallback(
    (row, index, isGrouped?: boolean) => (
      <CollapsedRow
        {...rowProps}
        hover={false}
        collapsible
        groupCollapse={isGrouped}
        size={size}
        dynamicHeight={dynamicHeight}
        fontSize={fontSize}
      >
        {headers.map(
          (header, i) =>
            (isGrouped || collapseCol(header.collapsible)) && (
              <TableCell header={header} index={i}>
                {!isGrouped && <TableTitle>{header.label}</TableTitle>}
                {shouldRenderToggleContent(header) &&
                  (header.type
                    ? renderCellContent[header.type](header, row, index)
                    : renderCellContent.string(header, row, index))}
              </TableCell>
            ),
        )}
      </CollapsedRow>
    ),
    [
      collapseCol,
      dynamicHeight,
      fontSize,
      headers,
      renderCellContent,
      rowProps,
      shouldRenderToggleContent,
      size,
    ],
  );

  const handleClickRow = useCallback(
    (row) => () => {
      if (window.getSelection().toString() || !checkClickableRow(row)) return;
      onClickRow(row);
    },
    [checkClickableRow, onClickRow],
  );

  const renderRows = useCallback(
    () =>
      processedData.map((row, index) => {
        const isLastRow = index === processedData.length - 1;
        return collapseRows ? (
          <TableCollapsibleRow {...rowProps} isLastRow={isLastRow}>
            <TableCollapsibleContainer>
              <Collapsible>
                {renderCollapsibleToggler(row, !!groupBy, index)}
                {groupBy && row.collapsedRows ? (
                  <>{row.collapsedRows.map((cr) => renderCollapsibleToggle(cr, index, true))}</>
                ) : (
                  renderCollapsibleToggle(row, index, false)
                )}
              </Collapsible>
            </TableCollapsibleContainer>
          </TableCollapsibleRow>
        ) : (
          <div role="presentation" onClick={handleClickRow(row)}>
            <TableData
              {...rowProps}
              pointer={checkClickableRow(row)}
              isLastRow={isLastRow}
              size={size}
              dynamicHeight={dynamicHeight}
              fontSize={fontSize}
            >
              {headers.map((header) => (
                <TableCell header={header} index={index}>
                  {header.type
                    ? renderCellContent[header.type](header, row, index)
                    : renderCellContent.string(header, row, index)}
                </TableCell>
              ))}
            </TableData>
          </div>
        );
      }),
    [
      processedData,
      collapseRows,
      rowProps,
      renderCollapsibleToggler,
      groupBy,
      renderCollapsibleToggle,
      handleClickRow,
      checkClickableRow,
      size,
      dynamicHeight,
      fontSize,
      headers,
      renderCellContent,
    ],
  );

  const handleChangePage = useCallback(
    (page: number) => {
      setDisableAllCheck(true);
      onChangePage(page);
    },
    [onChangePage],
  );

  return (
    <ThemeProvider theme={actualTheme}>
      <TableContainer>
        {renderHeaders()}
        {processedData.length > 0 ? (
          <>
            <RowsContainer
              rowCount={rowCount}
              ref={rowsContainerRef}
              size={size}
              fontSize={fontSize}
            >
              {renderRows()}
              {pagination === 'view-more-button' && hasMoreRows && (
                <ViewMoreButton viewMoreMessage={viewMoreMessage} onClick={loadMoreRows} />
              )}
            </RowsContainer>
            {pagination === 'paginator' && (
              <TablePaginatorContainer>
                <Paginator {...pages} onPageClick={handleChangePage} />
              </TablePaginatorContainer>
            )}
          </>
        ) : (
          <NoResultRow {...rowProps} hover={false} size={size} fontSize={fontSize}>
            <span data-testId={`${testId}-no-results`}>{noResultsText}</span>
          </NoResultRow>
        )}
      </TableContainer>
    </ThemeProvider>
  );
};

export default Table;
